import { getSettings } from "@/services/adminService";
import { clsx } from "clsx";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency in Indian Rupees
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export const formatLabel = (str) => {
  if (!str) return "";

  // Known payroll acronyms → always uppercase
  const ACRONYMS = [
    "epf",
    "esi",
    "pf",
    "tds",
    "lwf",
    "ctc",
    "hra",
    "da",
    "pt",
    "esi",
    "uans",
  ];

  return str
    .replace(/_/g, " ") // replace underscores with spaces
    .split(" ")
    .map((word) => {
      const lower = word.toLowerCase();
      // If word is a known acronym → return uppercased
      if (ACRONYMS.includes(lower)) return lower.toUpperCase();

      // Otherwise → capitalize normally
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

// Format number with Indian number system (lakhs, crores)
export function formatNumber(num) {
  return new Intl.NumberFormat('en-IN').format(num);
}

// Calculate percentage
export function calculatePercentage(value, total) {
  if (total === 0) return 0;
  return ((value / total) * 100).toFixed(1);
}

export function sanitizePhoneNumber(input) {
  if (!input) return "";

  // Allow: digits, +, -, space, (, )
  return input.replace(/[^0-9+\-\s()]/g, "");
}

// Calculate age from date of birth
export function calculateAge(dateOfBirth) {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

// Calculate experience in years and months
export function calculateExperience(joiningDate) {
  const today = new Date();
  const joinDate = new Date(joiningDate);

  let years = today.getFullYear() - joinDate.getFullYear();
  let months = today.getMonth() - joinDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  return { years, months };
}

// Format date for display (uses browser's local timezone)
export function formatDate(date, options = {}) {
  if (!date) return 'N/A';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  // Use browser's default locale for proper timezone conversion
  return new Date(date).toLocaleDateString(undefined, defaultOptions);
}


export function formatDateTime(date, options = {}) {
  if (!date) return 'N/A';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };

  // Use browser's default locale for proper timezone conversion
  return new Date(date).toLocaleString(undefined, defaultOptions);
}

// Format time only (uses browser's local timezone)
export function formatTime(date) {
  if (!date) return '';

  const d = new Date(date);

  let hours = d.getUTCHours();
  const minutes = d.getUTCMinutes();

  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')} ${period}`;
}

// Format date and time separately for display
export function formatDateTimeSeparate(date) {
  if (!date) return { date: 'N/A', time: '' };

  const dateObj = new Date(date);
  return {
    date: dateObj.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }),
    time: dateObj.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  };
}

// Generate employee ID
export function generateEmployeeId(prefix = 'EMP') {
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.random().toString(36).substr(2, 3).toUpperCase();
  return `${prefix}${timestamp}${random}`;
}

// Calculate gross salary
export function calculateGrossSalary(salaryStructure) {
  if (!salaryStructure) return 0;

  // Component-based salary support
  if (Array.isArray(salaryStructure.salary_components) && salaryStructure.salary_components.length > 0 && salaryStructure.use_component_based_salary) {
    return salaryStructure.salary_components
      .filter((c) => c && c.is_active !== false && c.category === 'earnings')
      .reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  }

  const {
    basic_salary = 0,
    house_rent_allowance = 0,
    hra = 0, // Legacy field
    medical_allowance = 0,
    leave_travel_allowance = 0,
    travel_allowance = 0, // Legacy field
    conveyance_allowance = 0,
    food_allowance = 0, // Legacy field
    performance_incentive = 0,
    internet_allowance = 0, // Legacy field
    other_benefits = 0,
    special_allowance = 0 // Legacy field
  } = salaryStructure;

  const toNum = (val) => Number(val) || 0;

  return (
    toNum(basic_salary) +
    toNum(house_rent_allowance || hra) +
    toNum(medical_allowance) +
    toNum(leave_travel_allowance || travel_allowance) +
    toNum(conveyance_allowance || food_allowance) +
    toNum(performance_incentive || internet_allowance) +
    toNum(other_benefits || special_allowance)
  );
}

// Calculate total deductions
export function calculateTotalDeductions(salaryStructure) {
  if (!salaryStructure) return 0;

  // Component-based salary support
  if (Array.isArray(salaryStructure.salary_components) && salaryStructure.salary_components.length > 0 && salaryStructure.use_component_based_salary) {
    return salaryStructure.salary_components
      .filter((c) => c && c.is_active !== false && c.category === 'deductions')
      .reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  }

  const {
    pf_employee = 0,
    esi_employee = 0,
    professional_tax = 0,
    tds = 0,
    loan_deductions = 0,
    others = 0
  } = salaryStructure;

  const toNum = (val) => Number(val) || 0;

  return toNum(pf_employee) + toNum(esi_employee) + toNum(professional_tax) + toNum(tds) + toNum(loan_deductions) + toNum(others);
}

// Calculate net salary
export function calculateNetSalary(salaryStructure) {
  const gross = calculateGrossSalary(salaryStructure);
  const deductions = calculateTotalDeductions(salaryStructure);
  return gross - deductions;
}

// Get status color
export function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'text-emerald-600 bg-emerald-500/10 hover:bg-emerald-500/20';
    case 'resigned':
      return 'text-amber-700 bg-amber-100 hover:bg-amber-200 dark:text-amber-200 dark:bg-amber-500/20 dark:hover:bg-amber-500/30';
    case 'terminated':
      return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-200 dark:bg-red-500/20 dark:hover:bg-red-500/30';
    case 'exit_scheduled':
      return 'text-orange-700 bg-orange-100 hover:bg-orange-200 dark:text-orange-200 dark:bg-orange-500/20 dark:hover:bg-orange-500/30';
    default:
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-500/20 dark:hover:bg-gray-500/30';
  }
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const getEndCol = (listLength) => {
  const startIndex = 1; // Column B (1)
  const endIndex = startIndex + (listLength - 1);
  return String.fromCharCode(65 + endIndex);
};

export const downloadSampleTemplate = async (showDepartmentDesignation) => {
  try {
    const settingsRes = await getSettings();
 
    const workLocations = settingsRes.data.work_locations?.map(i => i.name) || [];
    const departments = showDepartmentDesignation
      ? settingsRes.data.departments?.map(i => i.name) || []
      : [];

    const designations = showDepartmentDesignation
      ? settingsRes.data.designations?.map(i => i.name) || []
      : [];

    const shifts = settingsRes.data.shifts?.map(i => i.name) || [];

    const missing = [];
    if (!workLocations.length) missing.push("Work Locations");
    if (showDepartmentDesignation) {
      if (!departments.length) missing.push("Departments");
      if (!designations.length) missing.push("Designations");
    }
    if (!shifts.length) missing.push("Shifts");

    if (missing.length) {
      toast.error(
        `Please add the following before generating template: ${missing.join(", ")}`
      );
      return;
    }
 
    const staticLists = {
      Gender: ["male", "female", "other"],
      MaritalStatus: ["single", "married", "divorced", "widowed"],
      Status: ["active", "terminated", "resigned"]
    };
 
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Employees");
    const dropdownSheet = workbook.addWorksheet("Dropdowns");
    dropdownSheet.state = "veryHidden";
 
    // Add dropdown data to hidden sheet
    dropdownSheet.getRow(1).values = ["Gender", ...staticLists.Gender];
    dropdownSheet.getRow(2).values = ["MaritalStatus", ...staticLists.MaritalStatus];
    // Row 3 intentionally left unused (OnProbation removed — backend derives probation status).
    dropdownSheet.getRow(4).values = ["Status", ...staticLists.Status];
    dropdownSheet.getRow(5).values = ["WorkLocations", ...workLocations];
    if (showDepartmentDesignation) {
      dropdownSheet.getRow(6).values = ["Departments", ...departments];
      dropdownSheet.getRow(7).values = ["Designations", ...designations];
    }
    dropdownSheet.getRow(8).values = ["Shifts", ...shifts]; // ⭐ NEW ROW FOR SHIFTS

    const headers = [
      "Employee ID",
      "Name",
      "Email",
      "Phone",
      "Gender",
      "Date of Birth",
      "Aadhar Number",
      "PAN Number",
      "Marital Status",
      "Address",
      "Emergency Contact Name",
      "Emergency Contact Phone",
      "Blood Group",
    ];

    if (showDepartmentDesignation) {
      headers.push("Department", "Designation");
    }

    headers.push(
      "Shift",
      "Date of Joining",
      "Work Location",
      "Probation End Date",
      "Bank Name",
      "Account Number",
      "IFSC Code",
      "Branch",
      "Status"
    );

    /// Assign widths
    headers.forEach((header, index) => {
      sheet.getColumn(index + 1).width = 22;
    });

    // Add headers
    const headerRow = sheet.getRow(1);
    headerRow.values = headers;
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    const dummyRow = [
      "ET-MUM-0001",
      "John Doe",
      "john.doe@company.com",
      "9876543210",
      "male",
      "1990-01-15",
      "213456789012",
      "ABCDE1234F",
      "single",
      "123 Main Street, Mumbai",
      "Jane Doe",
      "9876543211",
      "O+",
    ];

    if (showDepartmentDesignation) {
      dummyRow.push(departments[0] || "", designations[0] || "");
    }

    dummyRow.push(
      shifts[0] || "",
      "2024-01-15",
      workLocations[0] || "",
      "2024-07-15",
      "State Bank of India",
      "1234567890",
      "SBIN0001234",
      "Mumbai Branch",
      "active"
    );

    sheet.getRow(2).values = dummyRow;

    const applyValidation = (colLetter, formula) => {
      for (let r = 2; r <= 500; r++) {
        sheet.getCell(`${colLetter}${r}`).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [formula],
          showErrorMessage: true,
          errorStyle: 'error',
          errorTitle: 'Invalid Entry',
          error: 'Please select a value from dropdown'
        };
      }
    };

    // Dropdown target columns are derived from `headers` rather than hard-coded.
    // The letters only held for the department-enabled layout: without that
    // feature there are two fewer columns, so the fixed letters landed the
    // Shift/Work Location/Status dropdowns on the wrong columns entirely.
    // Deriving them keeps both layouts correct and survives column changes.
    const colFor = (headerName) => {
      const index = headers.indexOf(headerName);
      return index === -1 ? null : String.fromCharCode(65 + index);
    };

    const validateColumn = (headerName, formula) => {
      const col = colFor(headerName);
      if (col) applyValidation(col, formula);
    };

    // Gender
    validateColumn("Gender", `Dropdowns!$B$1:$D$1`);
    // Marital status
    validateColumn("Marital Status", `Dropdowns!$B$2:$E$2`);
    // Status
    validateColumn("Status", `Dropdowns!$B$4:$D$4`);

    // Work Location
    const locEnd = getEndCol(workLocations.length);
    validateColumn("Work Location", `Dropdowns!$B$5:$${locEnd}$5`);

    if (showDepartmentDesignation) {
      const depEnd = getEndCol(departments.length);
      validateColumn("Department", `Dropdowns!$B$6:$${depEnd}$6`);
      const desEnd = getEndCol(designations.length);
      validateColumn("Designation", `Dropdowns!$B$7:$${desEnd}$7`);
    }

    // ⭐ SHIFT DROPDOWN VALIDATION
    const shiftEnd = getEndCol(shifts.length);
    validateColumn("Shift", `Dropdowns!$B$8:$${shiftEnd}$8`);

    // Generate File
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
 
    saveAs(blob, "Employee_Import_Template.xlsx");
    toast.success("Template downloaded successfully!");
  } catch (error) {
    console.error(error);
    toast.error("Failed to generate template!");
  }
};

export function formatEnumLabel(value = "") {
  if (!value) return "";

  return value
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
